import {
  Background,
  Controls,
  type Edge,
  Handle,
  MarkerType,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import '@xyflow/react/dist/style.css'
import { motion } from 'framer-motion'
import { ChevronRight, GitBranch, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { isMobileViewport } from '@/hooks/use-mobile'
import { apiClient } from '@/services/api'
import { cn } from '@/utils/cn'
import { getPipelineIcon } from './icon-map'

// Types
interface PipelineStep {
  ref: string
  template: string
  dependsOn: string[]
  name: string
  short_name: string
  description: string
  category: 'extract' | 'transform' | 'load'
  icon: string
  color: string
}

interface Pipeline {
  id: string
  name: string
  description: string
  category: string
  icon: string
  file_types: string[]
  step_count: number
  steps?: PipelineStep[]
}

interface PipelineSelectorProps {
  onSelect: (pipelineId: string) => void
  selectedPipelineId?: string
}

// Category colors for sidebar
const categoryColors: Record<string, string> = {
  'Case Extraction': 'from-amber-500/20 to-orange-500/10 border-amber-500/30',
  'Lake Indexing': 'from-teal-500/20 to-cyan-500/10 border-teal-500/30',
}

// Category badge colors
const categoryBadgeColors: Record<string, string> = {
  extract: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  transform: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
  load: 'bg-green-500/20 text-green-400 border-green-500/40',
}

// Custom Node Data Type
interface PipelineStepNodeData {
  name: string
  icon: string
  color: string
  category: string
  selected: boolean
  hasInput: boolean
  hasOutput: boolean
  [key: string]: unknown
}

// Custom Node Component
function PipelineStepNode({ data }: NodeProps<Node<PipelineStepNodeData>>) {
  const Icon = getPipelineIcon(data.icon)
  const categoryStyle = categoryBadgeColors[data.category] || ''

  return (
    <Card
      selected={data.selected}
      className={cn(
        'rounded-xl border-2 px-4 py-3 min-w-[180px] max-w-[220px]',
        'shadow-lg shadow-black/30 transition-all duration-200',
        !data.selected && 'hover:border-neutral-500'
      )}
      style={{
        borderColor: data.selected ? data.color : undefined,
        boxShadow: data.selected ? `0 0 20px ${data.color}40` : undefined,
      }}
    >
      {data.hasInput && (
        <Handle
          type="target"
          position={Position.Left}
          className="!w-3 !h-3 !bg-neutral-600 !border-2 !border-neutral-500"
        />
      )}

      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${data.color}25` }}>
          <Icon className="h-5 w-5" style={{ color: data.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-foreground text-sm truncate">{data.name}</span>
          </div>
          <Badge variant="outline" className={cn('text-[10px] mt-1', categoryStyle)}>
            {data.category.toUpperCase()}
          </Badge>
        </div>
      </div>

      {data.hasOutput && (
        <Handle
          type="source"
          position={Position.Right}
          className="!w-3 !h-3 !bg-neutral-600 !border-2 !border-neutral-500"
        />
      )}
    </Card>
  )
}

const nodeTypes = {
  pipelineStep: PipelineStepNode,
}

// Topologically sort steps into a single linear sequence, matching the
// orchestrator's serial execution order (see orchestrator.py).
function topologicalSort(steps: PipelineStep[]): PipelineStep[] {
  const stepMap = new Map(steps.map(s => [s.ref, s]))
  const visited = new Set<string>()
  const result: PipelineStep[] = []

  const visit = (step: PipelineStep) => {
    if (visited.has(step.ref)) return
    visited.add(step.ref)
    for (const dep of step.dependsOn) {
      const depStep = stepMap.get(dep)
      if (depStep) visit(depStep)
    }
    result.push(step)
  }

  for (const step of steps) visit(step)
  return result
}

// Render the pipeline as a linear chain: the orchestrator runs steps
// sequentially in topological order, so the visualization should too.
function buildFlowElements(steps: PipelineStep[], selectedStep: string | null) {
  const nodes: Node<PipelineStepNodeData>[] = []
  const edges: Edge[] = []
  const ordered = topologicalSort(steps)

  ordered.forEach((step, index) => {
    nodes.push({
      id: step.ref,
      type: 'pipelineStep',
      position: { x: index * 280 + 50, y: 150 },
      data: {
        ...step,
        selected: selectedStep === step.ref,
        hasInput: index > 0,
        hasOutput: index < ordered.length - 1,
      },
    })

    if (index > 0) {
      const prev = ordered[index - 1]
      edges.push({
        id: `${prev.ref}-${step.ref}`,
        source: prev.ref,
        target: step.ref,
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#525252', strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#525252',
        },
      })
    }
  })

  return { nodes, edges }
}

// Step Details Panel
function StepDetailsPanel({ step }: { step: PipelineStep | null }) {
  if (!step) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <GitBranch className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Click on a step to see its details</p>
      </div>
    )
  }

  const Icon = getPipelineIcon(step.icon)

  const categoryLabels = {
    extract: { label: 'Extract', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    transform: { label: 'Transform', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    load: { label: 'Load', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      key={step.ref}
      className="p-4 md:p-6"
    >
      <div className="flex items-start gap-3 mb-4 md:gap-4">
        <div className="p-3 rounded-lg" style={{ backgroundColor: `${step.color}20` }}>
          <Icon className="h-6 w-6" style={{ color: step.color }} />
        </div>
        <div className="flex-1 max-md:min-w-0">
          <h3 className="text-lg font-semibold text-foreground">{step.name}</h3>
          <p className="text-sm text-muted-foreground font-mono max-md:break-words">
            {step.template}
          </p>
        </div>
        <Badge variant="outline" className={categoryLabels[step.category].color}>
          {categoryLabels[step.category].label}
        </Badge>
      </div>

      <p className="text-foreground leading-relaxed">{step.description}</p>

      {step.dependsOn.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">Depends on:</p>
          <div className="flex flex-wrap gap-2">
            {step.dependsOn.map(dep => (
              <Badge key={dep} variant="outline" className="text-muted-foreground border-border">
                {dep}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  )
}

// Main Component
export function PipelineSelector({ onSelect }: PipelineSelectorProps) {
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null)
  const [selectedStep, setSelectedStep] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingDetails, setLoadingDetails] = useState(false)

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<PipelineStepNodeData>>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])

  // Fetch pipelines list
  useEffect(() => {
    const fetchPipelines = async () => {
      try {
        const data = await apiClient.get<any>(`/cases/pipeline-templates`)
        setPipelines(data)
      } catch (error) {
        console.error('Failed to fetch pipelines:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchPipelines()
  }, [])

  // Update React Flow when pipeline changes
  useEffect(() => {
    if (selectedPipeline?.steps) {
      const { nodes: newNodes, edges: newEdges } = buildFlowElements(
        selectedPipeline.steps,
        selectedStep
      )
      setNodes(newNodes)
      setEdges(newEdges)
    }
  }, [selectedPipeline, selectedStep, setNodes, setEdges])

  // Fetch pipeline details when selected
  const fetchPipelineDetails = useCallback(async (pipelineId: string) => {
    setLoadingDetails(true)
    setSelectedStep(null)
    try {
      const data = await apiClient.get<any>(`/cases/pipeline-templates/${pipelineId}`)
      setSelectedPipeline(data)
    } catch (error) {
      console.error('Failed to fetch pipeline details:', error)
    } finally {
      setLoadingDetails(false)
    }
  }, [])

  const handlePipelineSelect = (pipeline: Pipeline) => {
    fetchPipelineDetails(pipeline.id)
    onSelect(pipeline.id)
  }

  const handleNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedStep(node.id)
  }, [])

  // Group pipelines by category
  const groupedPipelines = useMemo(() => {
    const groups: Record<string, Pipeline[]> = {}
    pipelines.forEach(p => {
      if (!groups[p.category]) groups[p.category] = []
      groups[p.category].push(p)
    })
    return groups
  }, [pipelines])

  const currentStep = selectedPipeline?.steps?.find(s => s.ref === selectedStep) || null

  // A whole pipeline cannot fit a phone canvas at minZoom (the fit would centre it and
  // cut the first steps off), so the phone opens on the first two steps instead.
  const fitViewOptions = useMemo(
    () => ({ padding: 0.3, ...(isMobileViewport() && { nodes: nodes.slice(0, 2) }) }),
    [nodes]
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="flex h-[600px] bg-background rounded-lg border border-border overflow-hidden max-md:h-auto max-md:flex-col">
      {/* Sidebar */}
      <div className="w-72 border-r border-border flex flex-col max-md:h-72 max-md:w-full max-md:border-r-0 max-md:border-b">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-foreground">Processing Pipelines</h3>
          <p className="text-xs text-muted-foreground mt-1">Select a pipeline to see its steps</p>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-2">
            {Object.entries(groupedPipelines).map(([category, categoryPipelines]) => (
              <div key={category} className="mb-4">
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {category}
                </div>
                {categoryPipelines.map(pipeline => {
                  const Icon = getPipelineIcon(pipeline.icon)
                  const isSelected = selectedPipeline?.id === pipeline.id

                  return (
                    <motion.button
                      key={pipeline.id}
                      onClick={() => handlePipelineSelect(pipeline)}
                      className={cn(
                        'w-full text-left px-3 py-2.5 rounded-lg mb-1 transition-all',
                        'hover:bg-accent/50',
                        isSelected && 'bg-muted ring-1 ring-primary/50'
                      )}
                      whileHover={{ x: 4 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            'p-1.5 rounded-md bg-gradient-to-br',
                            categoryColors[category] || 'from-neutral-500/20 to-neutral-600/10'
                          )}
                        >
                          <Icon className="h-4 w-4 text-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {pipeline.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {pipeline.step_count} steps
                          </p>
                        </div>
                        {isSelected && <ChevronRight className="h-4 w-4 text-primary" />}
                      </div>
                    </motion.button>
                  )
                })}
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Main content - React Flow Canvas */}
      <div className="flex-1 flex flex-col max-md:h-[26rem] max-md:flex-none">
        <div className="flex-1 relative">
          {loadingDetails ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : selectedPipeline?.steps ? (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={handleNodeClick}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={fitViewOptions}
              proOptions={{ hideAttribution: true }}
              className="bg-background"
              minZoom={0.5}
              maxZoom={1.5}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={true}
            >
              <Background color="#262626" gap={20} size={1} />
              {/* Hidden on the phone: pinch and drag do the same without covering a step. */}
              <Controls className="max-md:!hidden !bg-muted !border-border !rounded-lg [&>button]:!bg-muted [&>button]:!border-border [&>button]:!text-muted-foreground [&>button:hover]:!bg-neutral-700" />
            </ReactFlow>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center">
                <GitBranch className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p>Select a pipeline from the sidebar</p>
                <p className="text-sm mt-1">to visualize its processing steps</p>
              </div>
            </div>
          )}
        </div>

        {/* Step details panel */}
        {selectedPipeline && (
          <div className="border-t border-border bg-card/50 min-h-[160px]">
            <StepDetailsPanel step={currentStep} />
          </div>
        )}
      </div>
    </div>
  )
}
